/**
 * use-foreign-menu-views — układy (formularz / lista) dla pozycji menu modułu wskazujących
 * kartotekę systemową SPOZA modułu (kreator ITE, decyzja 2026-10-05).
 *
 * Źródło układów: wspólna biblioteka kartoteki (`kartoteka_views`) + WERSJE ROBOCZE z tej sesji
 * (nowe / skopiowane / przemianowane / przeprojektowane układy) — te jadą do bazy razem z
 * „Zapisz menu" (`views[]` z `targetModuleCode`), nie wcześniej.
 *
 * Dla edytora pozycji każda obca kartoteka jest widziana jak encja-odwołanie
 * (`reference_to_system_entity`, slug = kod kartoteki) — dzięki temu ten sam akordeon
 * „Layout formularza / Widok listy" działa bez zmian, a wskaźnik pozycji ma kształt
 * `'<kodKartoteki>:<slugUkładu>'`.
 */

import { useCallback, useMemo, useState } from 'react';
import type {
  CreatorKartotekaLayoutsInfo,
  ListConfig,
  MenuItem,
  ModuleTemplateEntityDefinition,
  ModuleTemplateFormConfig,
  ModuleTemplateViewDefinition,
} from '@saas/shared';
import { findSystemKartotekaTarget } from '@saas/shared';
import { useQueries, useQueryClient } from '@tanstack/react-query';

import {
  fetchKartotekaLibraryViews,
  type KartotekaIdentityRef,
  kartotekaLibraryViewsQueryKey,
} from '@/lib/kartoteki/kartoteka-definition';
import type { TemplateMenuViewUpsert } from '@/lib/module-templates/template-layouts';

/** Wersja robocza układu obcej kartoteki (do zapisu razem z menu). */
export interface ForeignViewDraft {
  moduleCode: string;
  viewType: 'form' | 'list';
  slug: string;
  name: string;
  config: ModuleTemplateFormConfig | ListConfig | null;
  sortOrder: number;
}

const draftKey = (d: Pick<ForeignViewDraft, 'moduleCode' | 'viewType' | 'slug'>) =>
  `${d.moduleCode}:${d.viewType}:${d.slug}`;

/** Kartoteki z innych opublikowanych modułów kreatora z układami (z `menu-source`). */
export type CreatorTargets = Record<string, CreatorKartotekaLayoutsInfo> | undefined;

/**
 * Kod obcej kartoteki z układami albo `null` (własna kartoteka modułu, KSeF, link…):
 * kartoteka systemowa z katalogiem pól albo kartoteka z innego opublikowanego modułu kreatora.
 */
export function foreignLayoutsCode(
  item: MenuItem | null,
  templateSlug: string,
  creatorTargets?: CreatorTargets,
): string | null {
  if (!item || item.type !== 'module' || !item.moduleCode) return null;
  if (item.moduleCode.startsWith(`${templateSlug}_`)) return null;
  if (findSystemKartotekaTarget(item.moduleCode)?.hasLayouts) return item.moduleCode;
  return creatorTargets?.[item.moduleCode] ? item.moduleCode : null;
}

function collectCodes(
  items: MenuItem[],
  templateSlug: string,
  creatorTargets: CreatorTargets,
  acc: Set<string>,
): void {
  for (const item of items) {
    const code = foreignLayoutsCode(item, templateSlug, creatorTargets);
    if (code) acc.add(code);
    if (item.children?.length) collectCodes(item.children, templateSlug, creatorTargets, acc);
  }
}

/** Wskaźniki `kod:slug` układów obcych kartotek użyte w menu, per typ. */
function collectRefs(items: MenuItem[], acc: { form: Set<string>; list: Set<string> }): void {
  for (const item of items) {
    if (item.viewRef) acc.form.add(item.viewRef);
    if (item.listRef) acc.list.add(item.listRef);
    if (item.children?.length) collectRefs(item.children, acc);
  }
}

/** Slug nowego układu: prefiks typu + nazwa (jak układy kartotek modułu), unikalny. */
function slugFor(name: string, viewType: 'form' | 'list', taken: Set<string>): string {
  const prefix = viewType === 'form' ? 'form_' : 'list_';
  const base = `${prefix}${
    name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/ł/g, 'l')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 40) || 'widok'
  }`;
  let slug = base;
  for (let i = 2; taken.has(slug); i++) slug = `${base}_${i}`;
  return slug;
}

export function useForeignMenuViews(
  hierarchy: MenuItem[],
  templateSlug: string,
  creatorTargets?: CreatorTargets,
) {
  const queryClient = useQueryClient();
  const codes = useMemo(() => {
    const acc = new Set<string>();
    collectCodes(hierarchy, templateSlug, creatorTargets, acc);
    return [...acc].sort();
  }, [hierarchy, templateSlug, creatorTargets]);

  /** Biblioteka układów kartoteki: systemowa po slugu, z kreatora po UUID encji. */
  const identityOf = useCallback(
    (code: string): KartotekaIdentityRef => {
      const creator = creatorTargets?.[code];
      return creator ? { kind: 'creator', key: creator.entityId } : { kind: 'system', key: code };
    },
    [creatorTargets],
  );

  const libraryQueries = useQueries({
    queries: codes.map((code) => ({
      queryKey: kartotekaLibraryViewsQueryKey(identityOf(code)),
      queryFn: () => fetchKartotekaLibraryViews(identityOf(code)),
      retry: false,
      staleTime: 60_000,
    })),
  });

  const [drafts, setDrafts] = useState<ForeignViewDraft[]>([]);

  /** Kartoteki, których biblioteka się jeszcze nie wczytała (albo wczytanie padło). */
  const unloadedCodes = useMemo(
    () => new Set(codes.filter((_, i) => !libraryQueries[i]?.isSuccess)),
    [codes, libraryQueries],
  );
  /** Kartoteki, których biblioteka nie dała się wczytać (do komunikatu i ponownej próby). */
  const failedCodes = useMemo(
    () => new Set(codes.filter((_, i) => libraryQueries[i]?.isError)),
    [codes, libraryQueries],
  );
  const retryLibrary = useCallback(
    (code: string) => {
      void libraryQueries[codes.indexOf(code)]?.refetch();
    },
    [codes, libraryQueries],
  );
  const libraryHas = useCallback(
    (code: string, viewType: 'form' | 'list', slug: string) =>
      (libraryQueries[codes.indexOf(code)]?.data ?? []).some(
        (v) => v.viewType === viewType && v.slug === slug,
      ),
    [codes, libraryQueries],
  );

  /** Kod → układy (biblioteka nadpisana wersjami roboczymi). */
  const viewsByCode = useMemo(() => {
    const map = new Map<string, ModuleTemplateViewDefinition[]>();
    codes.forEach((code, i) => {
      const byKey = new Map<string, ModuleTemplateViewDefinition>();
      for (const v of libraryQueries[i]?.data ?? []) {
        const type = v.viewType as 'form' | 'list';
        byKey.set(`${type}:${v.slug}`, {
          slug: v.slug,
          name: v.name,
          type,
          isDefault: false,
          sortOrder: v.sortOrder,
          ...(type === 'form'
            ? { formConfig: (v.config as ModuleTemplateFormConfig | null) ?? undefined }
            : { listConfig: (v.config as ListConfig | null) ?? undefined }),
        } as ModuleTemplateViewDefinition);
      }
      for (const d of drafts.filter((x) => x.moduleCode === code)) {
        byKey.set(`${d.viewType}:${d.slug}`, {
          slug: d.slug,
          name: d.name,
          type: d.viewType,
          isDefault: false,
          sortOrder: d.sortOrder,
          ...(d.viewType === 'form'
            ? { formConfig: (d.config as ModuleTemplateFormConfig | null) ?? undefined }
            : { listConfig: (d.config as ListConfig | null) ?? undefined }),
        } as ModuleTemplateViewDefinition);
      }
      map.set(code, [...byKey.values()]);
    });
    return map;
  }, [codes, libraryQueries, drafts]);

  /**
   * Obce kartoteki jako encje dla edytora pozycji: systemowa = encja-odwołanie (pola z
   * definicji kartoteki), z kreatora = encja z polami z opublikowanej wersji modułu-źródła.
   */
  const pseudoEntities = useMemo<ModuleTemplateEntityDefinition[]>(
    () =>
      codes.map((code): ModuleTemplateEntityDefinition => {
        const creator = creatorTargets?.[code];
        if (creator) {
          return {
            slug: code,
            name: creator.label,
            sortOrder: 0,
            navigationMode: 'tab',
            ddlStrategy: 'new_table',
            fields: creator.fields.map((f) => ({
              slug: f.slug,
              name: f.name,
              type: f.type as ModuleTemplateEntityDefinition['fields'][number]['type'],
              isRequired: f.isRequired,
              sortOrder: f.sortOrder,
            })),
            views: viewsByCode.get(code) ?? [],
          };
        }
        return {
          slug: code,
          name: findSystemKartotekaTarget(code)?.label ?? code,
          sortOrder: 0,
          navigationMode: 'tab',
          ddlStrategy: 'reference_to_system_entity',
          referenceSystemModuleSlug: code,
          fields: [],
          views: viewsByCode.get(code) ?? [],
        };
      }),
    [codes, viewsByCode, creatorTargets],
  );

  const findView = useCallback(
    (code: string, viewType: 'form' | 'list', slug: string) =>
      (viewsByCode.get(code) ?? []).find((v) => v.type === viewType && v.slug === slug) ?? null,
    [viewsByCode],
  );

  const upsertDraft = useCallback((draft: ForeignViewDraft) => {
    setDrafts((prev) => [...prev.filter((d) => draftKey(d) !== draftKey(draft)), draft]);
  }, []);

  /** Nowy (albo kopia) układu — zwraca slug wersji roboczej. */
  const createDraft = useCallback(
    (
      code: string,
      viewType: 'form' | 'list',
      name: string,
      config: ModuleTemplateFormConfig | ListConfig | null,
    ): string => {
      const existing = viewsByCode.get(code) ?? [];
      const slug = slugFor(name, viewType, new Set(existing.map((v) => v.slug)));
      upsertDraft({
        moduleCode: code,
        viewType,
        slug,
        name,
        config,
        sortOrder: existing.filter((v) => v.type === viewType).length,
      });
      return slug;
    },
    [viewsByCode, upsertDraft],
  );

  /** Zmiana nazwy / treści istniejącego układu (biblioteka albo wersja robocza). */
  const updateView = useCallback(
    (
      code: string,
      viewType: 'form' | 'list',
      slug: string,
      patch: { name?: string; config?: ModuleTemplateFormConfig | ListConfig },
    ) => {
      const current = findView(code, viewType, slug);
      if (!current) return;
      upsertDraft({
        moduleCode: code,
        viewType,
        slug,
        name: patch.name ?? current.name,
        config:
          patch.config ?? (viewType === 'form' ? current.formConfig : current.listConfig) ?? null,
        sortOrder: current.sortOrder,
      });
    },
    [findView, upsertDraft],
  );

  /**
   * „Usuń" układu: nowy (tylko w tej sesji) → znika; układ z biblioteki ze zmianą w tej sesji
   * → zmiana cofnięta (`reverted`); układ z biblioteki bez zmian → nic (`library` — usuwa się
   * go na ekranie Kartoteki).
   */
  const removeDraft = useCallback(
    (code: string, viewType: 'form' | 'list', slug: string): 'removed' | 'reverted' | 'library' => {
      const key = draftKey({ moduleCode: code, viewType, slug });
      const hasDraft = drafts.some((d) => draftKey(d) === key);
      const inLibrary = libraryHas(code, viewType, slug);
      if (!hasDraft) return 'library';
      setDrafts((prev) => prev.filter((d) => draftKey(d) !== key));
      return inLibrary ? 'reverted' : 'removed';
    },
    [drafts, libraryHas],
  );

  /**
   * Wersje robocze → `views[]` PATCH menu. Jadą tylko zmiany układów z biblioteki oraz nowe
   * układy, na które menu wskazuje (bez sierot we wspólnej bibliotece). Lista bez kolumn nie
   * jedzie (serwer i tak odrzuci).
   */
  const upserts = useMemo<TemplateMenuViewUpsert[]>(() => {
    const refs = { form: new Set<string>(), list: new Set<string>() };
    collectRefs(hierarchy, refs);
    return drafts
      .filter(
        (d) =>
          libraryHas(d.moduleCode, d.viewType, d.slug) ||
          refs[d.viewType].has(`${d.moduleCode}:${d.slug}`),
      )
      .flatMap((d): TemplateMenuViewUpsert[] => {
        if (d.viewType === 'form') {
          return [
            {
              viewType: 'form',
              entitySlug: d.moduleCode,
              targetModuleCode: d.moduleCode,
              slug: d.slug,
              name: d.name,
              formConfig: (d.config as ModuleTemplateFormConfig | null) ?? { elements: [] },
              sortOrder: d.sortOrder,
            },
          ];
        }
        if (!d.config) return [];
        return [
          {
            viewType: 'list',
            entitySlug: d.moduleCode,
            targetModuleCode: d.moduleCode,
            slug: d.slug,
            name: d.name,
            listConfig: d.config as ListConfig,
            sortOrder: d.sortOrder,
          },
        ];
      });
  }, [drafts, hierarchy, libraryHas]);

  /** Po udanym zapisie menu: wersje robocze są już w bibliotece. */
  const commitDrafts = useCallback(() => {
    setDrafts([]);
    for (const code of codes) {
      void queryClient.invalidateQueries({
        queryKey: kartotekaLibraryViewsQueryKey(identityOf(code)),
      });
    }
  }, [codes, queryClient, identityOf]);

  return {
    pseudoEntities,
    findView,
    createDraft,
    updateView,
    removeDraft,
    upserts,
    unloadedCodes,
    failedCodes,
    identityOf,
    retryLibrary,
    hasDrafts: drafts.length > 0,
    commitDrafts,
  };
}
